import { useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import QueryChangeLogTable from "./QueryChangeLogTable";
import Loader from "../../components/Loader";
import useGetQueryChangeLogs from "../../hooks/useGetQueryChangeLogs";
import useGetQueryChangeLogsFile from "../../hooks/useGetQueryChangeLogsFile";

const QueryChangeLogs = () => {
  const { _id_ } = useParams();

  const { trialFilter, setActive, setBreadcrumbTitle } = useOutletContext() ?? {};
  setActive("QueryChangeLogs");
  setBreadcrumbTitle("eCRF Change Logs");

  const [fullScreenOpen, setFullScreenOpen] = useState(false);
  const [pageItemCount] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);

  const { data: queryLogs = [], isLoading: isLoadingQueryChangeLogs } = useGetQueryChangeLogs({
    page: pageNumber + 1,
    patientId: _id_,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  const { refetch: fetchQueryLogsFile, isFetching: isExporting } = useGetQueryChangeLogsFile({
    patientId: _id_,
    trialId: trialFilter,
    enabled: false,
  });

  const handlePageClick = (event) => {
    setPageNumber(event.selected);
  };

  const handleExportCSV = async () => {
    try {
      const res = await fetchQueryLogsFile();
      const fileUrl = res?.data?.file_url;

      if (fileUrl) {
        window.open(fileUrl, "_blank");
      }
    } catch (err) {
      console.error("Failed to export query logs", err);
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {isLoadingQueryChangeLogs ? (
        <Box display="flex" justifyContent="center" py={6}>
          <Loader />
        </Box>
      ) : (
        <>
          {/* Header Actions */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Query Change Logs</Typography>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleExportCSV}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "Export as Excel"}
              </Button>

              <Button variant="outlined" size="small" onClick={() => setFullScreenOpen(true)}>
                Full Screen View
              </Button>
            </Stack>
          </Stack>

          {/* Normal View */}
          <QueryChangeLogTable
            queryLogs={queryLogs}
            handlePageClick={handlePageClick}
            pageItemCount={pageItemCount}
            currentPage={pageNumber}
          />

          {/* Full Screen Dialog */}
          <Dialog open={fullScreenOpen} onClose={() => setFullScreenOpen(false)} fullScreen>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">Query Change Logs – Full Screen</Typography>

              <IconButton onClick={() => setFullScreenOpen(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 2 }}>
              <QueryChangeLogTable
                queryLogs={queryLogs}
                handlePageClick={handlePageClick}
                pageItemCount={pageItemCount}
                currentPage={pageNumber}
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </Paper>
  );
};

export default QueryChangeLogs;
